import { useEffect, useMemo } from 'react'
import type { JSX } from 'react'

import { Loader, SuccessDialog } from '@buffer-mono/popcorn'

import { getPublishUrl } from '@buffer-mono/shared-utils/urls'
import { useCrossAppNavigate } from '../../../../common/hooks/useCrossAppNavigate'

import { getUserChannelList } from '../../../../common/utils/user'

import { useUser } from '../../../../common/context/User'

import trackChannelConnectionModalViewed from '../../../../tracking/ChannelConnectionModal/trackChannelConnectionModalViewed'
import { cleanupUrlParams } from '../../utils'
import { useModalManager } from '../../../ModalManager/hooks/useModalManager'

import NewChannelRefreshSuccessDialog from './components/NewChannelRefreshSuccess'

import styles from './ChannelRefreshSuccess.module.css'
import { getReconnectingChannelCookie } from '../../../../exports/Orchestrator/channelConnections/utils'

const ChannelRefreshSuccessModal = (): JSX.Element => {
  const user = useUser()
  const navigateCrossApp = useCrossAppNavigate()

  const handleCreatePostCTA = ({
    socialChannelId,
    handleOnDismissModal,
  }: {
    socialChannelId: string
    handleOnDismissModal: () => void
  }): void => {
    const cta = 'channelRefreshSuccessModal-createAPostCta'
    handleOnDismissModal()
    navigateCrossApp(
      getPublishUrl(`post/new?channelId[]=${socialChannelId}&cta=${cta}`),
    )
  }

  const { modalData, dismissModal: closeAction } = useModalManager()
  const CTACopy = 'Create a Post'
  const intendedChannelIdForRefresh = useMemo(
    () => getReconnectingChannelCookie(),
    [],
  )

  useEffect(() => {
    trackChannelConnectionModalViewed({
      payload: {
        ctaButton: modalData?.ctaButton || '',
        ctaView: modalData?.ctaView || '',
        ctaLocation: modalData?.ctaLocation || '',
        ctaVersion: modalData?.ctaVersion,
        upgradePathName: modalData?.upgradePathName || '',
      },
      user,
      isReconnecting: true,
    })
    cleanupUrlParams()
  }, [user, modalData])

  const handleOnDismissModal = (): void => {
    closeAction()
  }

  if (
    !user?.currentOrganization?.billing ||
    !user?.currentOrganization?.id ||
    !modalData?.channelIds
  ) {
    return (
      <SuccessDialog open={true} onOpenChange={handleOnDismissModal}>
        <SuccessDialog.Content className={styles.dialog}>
          <Loader />
        </SuccessDialog.Content>
      </SuccessDialog>
    )
  }

  const socialChannelIds = modalData?.channelIds
  const usersChannelList = getUserChannelList(user)

  return (
    <NewChannelRefreshSuccessDialog
      intendedChannelIdForRefresh={intendedChannelIdForRefresh}
      handleOnDismissModal={handleOnDismissModal}
      channelName={modalData?.channelName}
      channelAvatar={modalData?.channelAvatar}
      usersChannelList={usersChannelList || []}
      socialChannelIds={socialChannelIds}
      CTACopy={CTACopy}
      handleCreatePostCTA={handleCreatePostCTA}
    />
  )
}

export default ChannelRefreshSuccessModal
